<script setup lang="ts">
import { computed } from 'vue';
import type { PageItem } from '@/db/content';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { useContentStore } from '@/stores/content';
import { getRootPathsAndDefaultLocale, getUrlForItem } from '@/navTree';
import type { ApiImage } from '@/types/images';
import { isOptimisedImage } from '@/types/images';
import { stripHtmlToText } from '@/utils/html';
import { getSortedNewsArticles } from '@/utils/newsArticles';
import NewsSummaryDisplay from './NewsSummaryDisplay.vue';
const MAX_ITEMS = 4;

const props = defineProps<{
  lists: Array<{
    title: string;
    icon: string;
    categories: Array<{ id: number; slug: string; name: string }>;
  }>;
}>();

const contentStore = useContentStore();

const currentLocale = useUiDisplayLocale();

const listingItems = computed(() => contentStore.expandedListingItems);

const navRoots = computed(() =>
  getRootPathsAndDefaultLocale(listingItems.value)
);

const sortedNewsArticles = computed(() => {
  return getSortedNewsArticles(listingItems.value, currentLocale.value);
});

function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function imageUrl(item: PageItem): string | undefined {
  const img = (item as { image?: ApiImage }).image;
  if (!img) return undefined;
  if (isOptimisedImage(img)) return img.fallback?.url ?? undefined;
  return 'url' in img ? (img as { url?: string }).url ?? undefined : undefined;
}

function imageAlt(item: PageItem): string {
  const img = (item as { image?: ApiImage }).image;
  const richPlain = stripHtmlToText(item.rich_title ?? '');
  return (img?.title ?? (richPlain || item.title || '')) as string;
}

const columns = computed(() => {
  const articles = sortedNewsArticles.value;
  const rootPathByLocale = navRoots.value.rootPathByLocale;
  const defaultLocale = navRoots.value.defaultLocale;
  const lists = props.lists ?? [];
  return lists.map((list) => {
    const catIds = new Set(list.categories.map((c) => c.id));
    let pool = articles;
    if (catIds.size > 0) {
      pool = articles.filter(
        (a) => a.category != null && catIds.has(a.category.id)
      );
    }
    const slice = pool.slice(0, MAX_ITEMS);
    const items = slice.map((item) => ({
      id: item.id,
      title: item.title ?? '',
      richTitle: item.rich_title ?? null,
      href: getUrlForItem(item, rootPathByLocale, defaultLocale),
      dateIso: item.date ?? null,
      dateLabel: formatDate(item.date ?? null),
      categoryName: item.category?.name ?? null,
      imageUrl: imageUrl(item) ?? null,
      imageAlt: imageAlt(item),
    }));
    return {
      title: list.title,
      iconKey: list.icon,
      items,
    };
  });
});
</script>

<template>
  <NewsSummaryDisplay :columns="columns" />
</template>
